<template>
  <section
    class="construction-params"
    :class="{ 'construction-params--with-filling': sector.filling }"
  >
    <article v-if="!isProAlgorithm" class="construction-params__forms">
      <template v-if="baseParams">
        <Structure
          :params="baseParams"
          type="base"
          :sector="sector"
          :visible="true"
          @defineValidity="defineValidity($event)"
        >
          <h5 class="construction-params__title">
            {{ $t('message.constructionParams.params.title') }}
          </h5>
        </Structure>
        <Structure
          v-if="screedParams"
          :params="screedParams"
          type="screed"
          :sector="sector"
          :visible="true"
          @defineValidity="defineValidity($event)"
        />
      </template>
    </article>
    <article v-else class="construction-params__forms">
      <Structure
        v-for="structure of proStructures"
        :params="structure.source"
        :type="structure.type"
        :sector="sector"
        :key="structure.id"
        :order="structure.specialOrder"
        :visible="showStructure(structure)"
        @defineValidity="defineValidity($event)"
      >
        <h5 v-if="showParamsTitle(structure)" class="construction-params__title">
          {{ getStructureTitle(structure) }}
        </h5>
        <template v-if="showLayerTitle(structure)" v-slot:subtitle>
          <h6 class="structure__layer-title" :style="`grid-area: ${structure.subtitle}`">
            {{ $t(`message.constructionParams.facingParams.layers.${structure.subtitle}`) }}
          </h6>
        </template>
      </Structure>
    </article>
    <Index
      :sectorIndexes="sector.indexes"
      v-if="readyParams || !baseParams"
      :isPRO="isProAlgorithm"
      :sector="sector"
    />
    <template>
      <Recap :indexes="sector.indexes" v-if="showAdvices" :sector="sector" :is-result-page="false"/>
      <ErrorNotification
        v-if="isGettingErrors"
        :notifications="errorNotifications"
        :constructionType="sector.constructionType.type"
      />
      <Constructions
        v-if="isThereOptionalSystem && !isGettingErrors && (readyParams || !baseParams)"
        :constructions="sector.optionalSystems"
        :selectedConstructions="sector.construction"
        @handle="setActiveConstruction($event)"
      />
    </template>
  </section>
</template>

<script>
import Index from '@/components/dump/IndexComponent'
import Structure from '@/components/smart/ConstructionStructure'
import { mapActions, mapGetters } from 'vuex'
import Constructions from '@/components/smart/Constructions'
import { getSuitableSystems } from '@/api'
import { CalcTypes, ConstructionTypes, Layers, SizeParams, Structures } from '@/utils/types'
import ErrorNotification from '@/components/dump/ErrorNotification.vue'
import DropdownConfigsMixin from '@/components/mixin/DropdownConfigsMixin.vue'
import Recap from '@/components/dump/RecapComponent.vue'

export default {
  name: 'ConstructionParams',
  props: ['sector'],
  mixins: [DropdownConfigsMixin],
  components: { Recap, Constructions, Index, Structure, ErrorNotification },
  watch: {
    selectedOptionalSystem: function (newValue) {
      if (newValue !== null && !this.isFloorPRO && !this.hasSizesBlock) {
        this.getSuitableSystems(this.selectedOptionalSystem)
      }
    },
    readyParams: function (newValue, oldValue) {
      if (!this.isProAlgorithm && oldValue && !newValue) {
        this.updateSectorParams('isCalculated', false)
      }
    },
    base: function () {
      this.reset()
    },
    screed: function () {
      this.reset()
    },
    facing: function () {
      this.reset()
    },
    isolation: function () {
      this.reset()
    },
    soundIsolation: function () {
      this.reset()
    },
    sizeParams: function () {
      this.reset()
    },
    facingInnerLayer: function () {
      this.reset()
    },
    plaster: function () {
      this.reset()
    }
  },
  mounted() {
    if (this.sector.structures && this.sector.structures.length === 0) {
      this.getOptionalSystem(this.sectorIndex, true)
    }
  },
  computed: {
    ...mapGetters({
      getConstructionTypes: 'getConstructionTypes',
      sizesGetter: 'sizesGetter',
      plasterGetter: 'plasterGetter'
    }),
    sectorIndex() {
      return this.$store.state.sectors.findIndex(sector => sector.id === this.sector.id)
    },
    errorNotifications() {
      return this.sector.errors
    },
    selectedOptionalSystem() {
      return this.sector.construction
    },
    constructionStructure() {
      return this.sector.structures
    },
    baseParams() {
      return this.getStructure(this.$t('message.constructionParams.params.base'))
    },
    screedParams() {
      return this.getStructure(this.$t('message.constructionParams.params.screed'))
    },
    facingParams() {
      return this.getStructure(this.$t('message.constructionParams.facingParams.constructionGroup'))
    },
    isolationParams() {
      return this.getStructure(
        this.$t('message.constructionParams.isolationParams.constructionGroup')
      )
    },
    plasterParams() {
      return this.getStructure(
        this.$t('message.constructionParams.plasterParams.constructionGroup')
      )
    },
    soundIsolationParams() {
      return this.getStructure(
        this.$t('message.constructionParams.soundIsolationParams.constructionGroup')
      )
    },
    sizes() {
      return this.getStructure(this.$t('message.constructionParams.sizeParams.constructionGroup'))
    },
    base() {
      return this.sector.base
    },
    screed() {
      return this.sector.screed
    },
    facing() {
      return this.sector.facing
    },
    isolation() {
      return this.sector.isolation
    },
    plaster() {
      return this.sector.plaster
    },
    showPlaster() {
      return this.sector.plaster.plasterNotBlocked
    },
    soundIsolation() {
      return this.sector.soundIsolation
    },
    sizeParams() {
      return this.sector.sizes
    },
    facingInnerLayer() {
      return this.sector.facingInnerLayer
    },
    isProAlgorithm() {
      return this.sector.algorithm === CalcTypes.pro
    },
    isWallPRO() {
      return this.isProAlgorithm && this.currentConstructionType === ConstructionTypes.wall
    },
    isFloorPRO() {
      return this.isProAlgorithm && this.isFloor
    },
    isFloor() {
      return this.currentConstructionType === ConstructionTypes.floor
    },
    isCeiling() {
      return this.currentConstructionType === ConstructionTypes.ceiling
    },
    readyParams() {
      const type = () => {
        if (this.currentConstructionType === ConstructionTypes.wall) {
          if (this.hasPlasterBlock) {
            return ConstructionTypes.wall
          }

          return this.hasSizesBlock ? ConstructionTypes.wall : null
        }

        return this.sector.constructionType.type
      }

      const method = this.isProAlgorithm
        ? () =>
          this.getCalculationResult({
            index: this.sectorIndex,
            type: type(),
            isWithSizes: this.hasSizesBlock,
            isWithPlaster: this.hasPlasterBlock
          })
        : () => this.getOptionalSystem(this.sectorIndex)
      const isReady = !this.hasUnfilledParams() && !this.hasUnfilledSizesParams()

      if (isReady && this.sector.isValid) {
        method()
      }

      return isReady
    },
    isThereOptionalSystem() {
      return this.sector.optionalSystems?.length
    },
    currentConstructionType() {
      return this.sector.constructionType.type
    },
    isGettingErrors() {
      return this.errorNotifications
        ? Object.values(this.errorNotifications).some(error => error.needRender)
        : false
    },
    proStructures() {
      const proWallOrCeilingSet = [
        { key: 'params', type: Structures.base, source: this.baseParams },
        { key: 'sizeParams', type: Structures.sizes, source: this.sizeParams },
        {
          key: 'facingParams',
          type: Structures.facing,
          source: this.facingParams,
          subtitle: Layers.outer
        },
        {
          key: Structures.facingInnerLayer,
          type: Structures.facingInnerLayer,
          source: this.facingParams,
          subtitle: Layers.inner
        },
        { key: 'plasterParams', type: Structures.plaster, source: this.plasterParams },
        { key: 'isolationParams', type: Structures.isolation, source: this.isolationParams }
      ]

      const structureSets = {
        [ConstructionTypes.floor]: [
          { key: 'params', type: Structures.base, source: this.baseParams },
          { key: '', type: Structures.soundIsolation, source: this.soundIsolationParams },
          { key: '', type: Structures.screed, source: this.screedParams }
        ],
        [ConstructionTypes.wall]: proWallOrCeilingSet,
        [ConstructionTypes.ceiling]: proWallOrCeilingSet
      }

      if (
        [ConstructionTypes.wall, ConstructionTypes.ceiling].includes(this.currentConstructionType)
      ) {
        if (this.hasPlasterBlock) {
          const filterStructuresSets = structureSets[this.currentConstructionType].filter(
            structureBlock =>
              ![Structures.sizes, Structures.facingInnerLayer, Structures.facing].includes(
                structureBlock.type
              )
          )

          return filterStructuresSets
        } else {
          const filterStructuresSets = structureSets[this.currentConstructionType].filter(
            structureBlock => ![Structures.plaster].includes(structureBlock.type)
          )

          return this.hasSizesBlock
            ? filterStructuresSets.map(structureBlock => {
              if (structureBlock.type === Structures.facing) {
                structureBlock.specialOrder = this.orderSets[this.currentConstructionType][Structures.facing]
              }

              return structureBlock
            })
            : structureSets[this.currentConstructionType].filter(
              structureBlock =>
                ![Structures.sizes, Structures.base, Structures.plaster].includes(
                  structureBlock.type
                )
            )
        }
      }

      return structureSets[this.currentConstructionType]
    },
    hasSizesBlock() {
      return this.sector.system?.isSizeable && this.isProAlgorithm
    },
    hasPlasterBlock() {
      return this.sector.system?.isPlaster && this.isProAlgorithm
    },
    showAdvices() {
      return (this.sector.isCalculated && this.hasSizesBlock) || (this.sector.isCalculated && this.hasPlasterBlock)
    }
  },
  methods: {
    ...mapActions({
      getOptionalSystem: 'getOptionalSystem',
      setCalculatedIndexes: 'setCalculatedIndexes',
      getCalculationResult: 'getCalculationResult',
      setTestingStatus: 'setTestingStatus'
    }),
    showLayerTitle(structure) {
      const hasWithSizes = this.hasSizesBlock && (this.isWallPRO || this.isCeiling)
      const layerBlocks = structure.type === Structures.facing || structure.type === Structures.facingInnerLayer

      return hasWithSizes && layerBlocks && this.sector.facing?.sheets > 1
    },
    showStructure(structure) {
      if (structure.type !== Structures.facingInnerLayer) {
        return true
      }

      return this.showLayerTitle(structure)
    },
    setActiveConstruction(evt) {
      this.updateSectorParams('construction', evt.value)
      if (!this.isProAlgorithm) this.updateSectorParams('isCalculated', true)
    },
    getSuitableSystems(system) {
      this.setCalculatedIndexes({
        index: this.sectorIndex,
        payload: { rw: system?.rw, lnw: system?.lnw }
      })
      this.setTestingStatus({ index: this.sectorIndex, payload: system?.isTested })

      const requestData = {
        optionSystemId: this.sector.construction?.id,
        rw: this.sector.indexes.calculated?.rw,
        lnw: this.sector.indexes.calculated?.lnw,
        rwRequired: this.sector.indexes.required?.rw,
        lnwRequired: this.sector.indexes.required?.lnw
      }

      getSuitableSystems(requestData).then(response => {
        const errors = {
          rw: {
            text: response.data.rw === '' ? null : response.data.rw,
            needRender: response.data.rw !== null && response.data.rw !== ''
              ? requestData.rw <= requestData.rwRequired
              : false
          },
          lnw: {
            text: response.data.lnw,
            needRender:
              response.data.lnw !== null ? requestData.lnw >= requestData.lnwRequired : false
          }
        }

        this.updateSectorParams('errors', errors)

        this.isGettingErrors
          ? this.updateSectorParams('optionalSystems', null)
          : this.updateSectorParams('isCalculated', true)
      })
    },
    updateSectorParams(property, value) {
      this.$store.commit('UPDATE_SECTOR', { index: this.sectorIndex, property, value })
    },
    resetErrors() {
      this.updateSectorParams('errors', null)
      this.updateSectorParams('image', null)
    },
    resetCalculationStatus() {
      if (this.sector.isCalculated) {
        this.updateSectorParams('isCalculated', false)
      }
    },
    reset() {
      this.updateSectorParams('construction', null)
      this.updateSectorParams('optionalSystems', null)
      this.resetCalculationStatus()
      this.resetErrors()
    },
    hasUnfilledParams() {
      const requiredParams = {
        [CalcTypes.pro]: {
          [ConstructionTypes.wall]: [Structures.facing, Structures.isolation, Structures.base],
          [ConstructionTypes.floor]: [
            Structures.base,
            Structures.soundIsolation,
            Structures.screed
          ],
          [ConstructionTypes.ceiling]: [Structures.base, Structures.isolation, Structures.facing]
        },
        [CalcTypes.lite]: {
          [ConstructionTypes.wall]: [Structures.base],
          [ConstructionTypes.floor]: [Structures.base, Structures.screed]
        }
      }

      let currentParamsSet = requiredParams[this.sector.algorithm][this.currentConstructionType]

      if (this.isWallPRO || this.isCeiling) {
        if (this.hasSizesBlock) {
          const layerParam = Structures.facingInnerLayer
          const hasLayerParams = currentParamsSet.find(param => param === layerParam)
          const needInclude =
            this.sector.facing.sheets > 1 && !this.facingInnerLayer.facingInnerLayerBlocked

          if (!hasLayerParams && needInclude) {
            currentParamsSet = currentParamsSet.concat([Structures.facingInnerLayer])
          } else if (hasLayerParams && !needInclude) {
            currentParamsSet = currentParamsSet.filter(param => param === layerParam)
          }
        } else {
          currentParamsSet = currentParamsSet.filter(param => param !== Structures.base)
        }

        if (this.hasPlasterBlock) {
          currentParamsSet = currentParamsSet
            .filter(param => param !== Structures.facing)
            .concat([Structures.base])
        }
      }

      return currentParamsSet.some(
        param =>
          Object.entries(this[param]).filter(([key, value]) => !key.includes('Blocked') && !value)
            .length !== 0
      )
    },
    getStructure(name) {
      return this.constructionStructure?.find(structure => structure.constructionGroup === name)
    },
    hasUnfilledSizesParams() {
      if (!this.hasSizesBlock) {
        return false
      }

      const sizesParams = {
        [ConstructionTypes.wall]: [SizeParams.x, SizeParams.y],
        [ConstructionTypes.ceiling]: [SizeParams.x, SizeParams.y],
        [ConstructionTypes.floor]: []
      }
      const sizes = this.sizesGetter(this.sectorIndex)

      return sizesParams[this.currentConstructionType].length === 0 ||
        !this.isProAlgorithm ||
        sizes.sizesBlocked
        ? false
        : Object.keys(sizes)
          .filter(key => sizesParams[this.currentConstructionType].includes(key))
          .map(value => sizes[value])
          .filter(value => !value).length !== 0
    },
    getStructureTitle(structure) {
      const { key, type } = structure
      const translateKey =
        key === 'sizeParams' &&
        type === Structures.sizes &&
        this.sector.constructionType.type === ConstructionTypes.ceiling
          ? ConstructionTypes.ceiling
          : structure.key

      return this.$t(`message.constructionParams.${translateKey}.title`)
    },
    showParamsTitle(structure) {
      return structure.type === Structures.plaster
        ? this.showPlaster
        : structure.key && structure.key !== Structures.facingInnerLayer
    }
  }
}
</script>

<style scoped lang="sass">
.construction-params
  display: flex
  flex-wrap: wrap
  width: 100%
  &--with-filling
    .construction-params__forms
      order: 1
    .filling
      order: 2
      +media((margin-bottom: (0: rem(10), 768: 0)))
    .indexes
      order: 3
    .constructions
      order: 4
  &__title
    margin: 0 0 rem(20)
    font-weight: 600
    line-height: 1.3
  &__forms
    width: 100%
  &__forms + .indexes:has(.indexes__list:not(:empty))
    +media((margin-top: (0: rem(10), 768: rem(30))))
  .filling + .constructions
    margin-top: rem(30)

.construction-params__forms:has(.structure:last-child .dropdown.dropdown--with-tooltip:last-child) + .indexes
  +media((margin-top: (0: -30px, 768: -10px, 800: -19px, 1230: -34px)))
</style>
